import { AddCardIcon, CashIcon, Title } from "lola-framework-ui-test";
import { PayoutInfo } from "./PayoutInfo";
import { useStore } from "../../../../store/globalState";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../../constants";
import { paymentType } from "../../../../interfaces/payment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../store/useSessionStore";
import { getAllPaymentMethods } from "../../../../services/api";
import type { ICardPaymentMethod } from "../../../../interfaces/payment-method";
import masterCardLogo from "../../../../assets/master_card_icon.png";
import visaLogo from "../../../../assets/visa_color_icon.png";

export interface SelectPaymentMethodSlotProps {
  onClose: () => void;
}

export const SelectPaymentMethodSlot: React.FC<
  SelectPaymentMethodSlotProps
> = ({ onClose }) => {
  const { t } = useTranslation();
  const { theme, setPaymentState, cardList, exchangeRate } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn } = colorConfig;
  const navigate = useNavigate();

  const allowedMethods = session.data?.config?.allowedFundingMethods ?? [];
  const showCash = allowedMethods.includes("cash");
  const showCards = allowedMethods.includes("card");

  const [cards, setCards] = useState<ICardPaymentMethod[]>([]);

  useEffect(() => {
    const linkId = session.data?.linkId;
    if (!linkId) return;

    getAllPaymentMethods(linkId)
      .then((methods) => {
        setCards(methods.cards.filter((c) => c.enabled));
      })
      .catch((err) => {
        console.error("Failed to fetch payment methods:", err);
        navigate(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      });
  }, [session.data?.linkId, cardList, navigate]);

  const onSelectPayment = ({
    cardId,
    type,
    brand,
  }: {
    cardId: string | null;
    type: paymentType;
    brand: string;
  }) => {
    setPaymentState({
      payment: {
        cardId,
        type,
        brand,
      },
    });
    onClose();
  };

  const pickIcon = useCallback((brand: string) => {
    let finalIcon = null;
    if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
    if (brand.toLocaleUpperCase().includes("MASTERCARD"))
      finalIcon = masterCardLogo;

    return {
      brand,
      icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
    };
  }, []);

  return (
    <section className="select-payer-slot">
      <Title title={t("payment.selectMethod")} align="center" />
      <section
        className="select-payer-list"
        style={
          {
            "--bgIcon": backgroundBtn,
          } as React.CSSProperties
        }
      >
        {showCash && (
          <PayoutInfo
            title={t("payment.cashDeposit")}
            subtitle={t("payment.minMax", {
              min: exchangeRate?.lowerLimit ?? "0",
              max: exchangeRate?.upperLimit ?? "0",
            })}
            icon={<CashIcon size={20} />}
            fee={
              <label className="footer-text foreground">
                {t("payment.fee", { amount: "$3.11" })}
              </label>
            }
            onChange={() =>
              onSelectPayment({
                cardId: null,
                type: paymentType.CASH,
                brand: t("payment.cashDeposit"),
              })
            }
          />
        )}
        {showCards && (
          <>
            {cards.map((card) => {
              const displayName = `${card.brand}-${card.last4}`;
              return (
                <PayoutInfo
                  key={card.paymentMethodId}
                  title={displayName}
                  subtitle={t("payment.minMax", {
                    min: exchangeRate?.lowerLimit ?? "0",
                    max: exchangeRate?.upperLimit ?? "0",
                  })}
                  icon={pickIcon(card.brand).icon}
                  fee={
                    <label className="footer-text">
                      {t("payment.fee", { amount: "$2.10" })}
                    </label>
                  }
                  onChange={() =>
                    onSelectPayment({
                      cardId: card.paymentMethodId,
                      type: paymentType.CARD,
                      brand: displayName,
                    })
                  }
                />
              );
            })}
            <PayoutInfo
              title={t("payment.addDebitCard")}
              subtitle={t("payment.minMax", {
                min: exchangeRate?.lowerLimit ?? "0",
                max: exchangeRate?.upperLimit ?? "0",
              })}
              icon={
                <AddCardIcon size={20} colors={[textColorBtn, textColorBtn]} />
              }
              fee={
                <label className="footer-text foreground">
                  {t("payment.fee", { amount: "$2.10" })}
                </label>
              }
              onChange={() => navigate(APP_ROUTES.ADD_CARD_PAGE)}
            />
          </>
        )}
      </section>
    </section>
  );
};
